import { LockOutlined } from "@ant-design/icons";
import { Button, Col, Flex, Form, Modal, Row } from "antd";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { createUseStyles } from "react-jss";
import useCustomerManagementApi from "../apiHooks/useCustomerManagementApi";
import useUserManagementApi from "../apiHooks/useUserManagementApi";
import PasswordInput from "../common/components/passwordInput";
import { _STYLES } from "../common/utils/customStyles";
import { useNotificationContext } from "../core/contexts/NotificationContextProvider";

const { mt1, textSm3, uploadLogo } = _STYLES;
const useStyles = createUseStyles({
  mt1,
  textSm3,
  uploadLogo,
});

//type = user | customer
const ResetPasswordModal = forwardRef(
  ({ isModelOpen, toggleModal, cbOnSave, type }, ref) => {
    const classes = useStyles();

    const [recordId, setRecordId] = useState(null);

    const { notify } = useNotificationContext();
    const [form] = Form.useForm();

    const {
      resetPasswordApi: {
        execute: resetUserPassword,
        isLoading: isUserResetPasswordLoading,
      },
    } = useUserManagementApi();
    const {
      changePasswordApi: {
        execute: resetCustomerPassword,
        isLoading: isCustomerResetPasswordLoading,
      },
    } = useCustomerManagementApi();

    useImperativeHandle(ref, () => ({
      async setRecordId(data) {
        setRecordId(data);
      },
    }));

    const handleSubmit = useCallback(
      async (data) => {
        if (data.ConfirmPassword !== data.NewPassword) {
          notify({
            type: "error",
            message: "New Passwords do not match",
          });
          return;
        }

        const res = await (type === "customer"
          ? resetCustomerPassword({ id: recordId, password: data.NewPassword })
          : resetUserPassword({
              UserId: recordId,
              OldPassword: data.NewPassword,
              NewPassword: data.NewPassword,
            }));

        if (res) {
          toggleModal();
          notify({
            message: "Password reset successfully",
          });
          cbOnSave?.();
        }
      },
      [
        cbOnSave,
        notify,
        recordId,
        resetCustomerPassword,
        resetUserPassword,
        toggleModal,
        type,
      ]
    );

    useEffect(() => {
      if (!isModelOpen) {
        form.resetFields();
        setRecordId(null);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModelOpen]);

    return (
      <Modal
          centered
        title="Reset Password"
        open={isModelOpen}
        onCancel={() => toggleModal()}
        footer={null}
      >
        <Row className={classes.mt1}>
          <Col xs={24}>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              autoComplete="off"
            >
              <Form.Item
                label="New Password"
                name="NewPassword"
                rules={[
                  {
                    required: true,
                    message: "Please enter your new password",
                  },
                ]}
              >
                <PasswordInput placeholder="New Password" />
              </Form.Item>
              <Form.Item
                label="Confirm New Password"
                name="ConfirmPassword"
                rules={[
                  {
                    required: true,
                    message: "Please confirm your new password",
                  },
                ]}
              >
                <PasswordInput placeholder="Confirm Password" />
              </Form.Item>

              <Flex justify="flex-end">
                <Button
                  loading={
                    isUserResetPasswordLoading || isCustomerResetPasswordLoading
                  }
                  type="primary"
                  htmlType="submit"
                  icon={<LockOutlined />}
                >
                  Submit
                </Button>
              </Flex>
            </Form>
          </Col>
        </Row>
      </Modal>
    );
  }
);

export default ResetPasswordModal;
